/**
 * client/poki.ts
 * Responsabilidad: Wrapper del SDK de Poki para rewarded ads y commercial breaks.
 *
 * Si el SDK no esta disponible (localhost, adblocker), todas las funciones degradan silenciosamente.
 */
import { isCrazyGamesEnvironment } from "./crazygames";

declare global {
    interface Window {
        PokiSDK?: {
            init: () => Promise<void>;
            gameLoadingFinished: () => void;
            gameplayStart: () => void;
            gameplayStop: () => void;
            commercialBreak: () => Promise<void>;
            rewardedBreak: () => Promise<boolean>;
        };
    }
}

let pokiReady = false;
let adBlockerActive = false;

/**
 * Dominios autorizados por Poki.
 * VACIO hasta que Poki apruebe un dominio oficial.
 * Cuando Poki apruebe (ej. alchem-io.com), añadir aqui.
 */
const POKI_AUTHORIZED_DOMAINS: string[] = [
    // "alchem-io.com",  // Descomentar cuando Poki apruebe
    // "alchem.io",
];

export function isPokiAuthorizedDomain(): boolean {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    return POKI_AUTHORIZED_DOMAINS.some(
        (domain) => host === domain || host.endsWith("." + domain)
    );
}

export async function initPoki(): Promise<void> {
    if (isCrazyGamesEnvironment()) {
        console.log("[Poki] CrazyGames environment detected. Poki SDK disabled.");
        return;
    }
    if (!isPokiAuthorizedDomain()) {
        console.log("[Poki] Domain not authorized by Poki. Poki SDK disabled.");
        adBlockerActive = true;
        return;
    }
    if (typeof window.PokiSDK === "undefined") {
        console.log("[Poki] SDK not available (adblocker or local).");
        adBlockerActive = true;
        return;
    }
    try {
        await window.PokiSDK.init();
        pokiReady = true;
        console.log("[Poki] SDK inicializado.");
    } catch (err) {
        console.warn("[Poki] Error al inicializar:", err);
        adBlockerActive = true;
    } finally {
        try { window.PokiSDK.gameLoadingFinished(); } catch { /* ignore */ }
    }
}

export function isPokiReady(): boolean {
    if (isCrazyGamesEnvironment()) return false;
    if (!isPokiAuthorizedDomain()) return false;
    return pokiReady;
}

export function isAdBlockerActive(): boolean {
    if (isCrazyGamesEnvironment()) return false;
    if (!isPokiAuthorizedDomain()) return false;
    return adBlockerActive;
}

export function gameplayStart(): void {
    if (isCrazyGamesEnvironment()) return;
    if (!isPokiAuthorizedDomain()) return;
    try { window.PokiSDK?.gameplayStart(); } catch { /* ignore */ }
}

export function gameplayStop(): void {
    if (isCrazyGamesEnvironment()) return;
    if (!isPokiAuthorizedDomain()) return;
    try { window.PokiSDK?.gameplayStop(); } catch { /* ignore */ }
}

export async function showRewardedAd(): Promise<boolean> {
    if (isCrazyGamesEnvironment()) return false;
    if (!isPokiAuthorizedDomain()) return false;
    if (!pokiReady) return false;
    try {
        const result = await window.PokiSDK!.rewardedBreak();
        return result === true;
    } catch (err) {
        console.warn("[Poki] Error en rewarded break:", err);
        return false;
    }
}

export async function showCommercialBreak(): Promise<void> {
    if (isCrazyGamesEnvironment()) return;
    if (!isPokiAuthorizedDomain()) return;
    if (!pokiReady) return;
    try {
        await window.PokiSDK!.commercialBreak();
    } catch (err) {
        console.warn("[Poki] Error en commercial break:", err);
    }
}
